import React from "react";

/**
 * Top-down multi-timeframe context for the charted symbol.
 *
 * Reads the same ladder the AI research trader consumes, so the operator and
 * the model are never looking at independently derived numbers that can
 * disagree. One compact chip per timeframe rather than six charts: direction,
 * position within that timeframe's range, and net move in ATR units are what
 * a trader actually reads off a higher timeframe.
 *
 * Clicking a chip switches the main chart when the timeframe is one the
 * chart itself supports.
 */

const TREND_STYLES = {
  up: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
  down: "border-red-500/40 bg-red-500/10 text-red-200",
  flat: "border-white/15 bg-white/5 text-white/60",
};

const TREND_GLYPH = { up: "↑", down: "↓", flat: "→" };

const formatSigned = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const rounded = Math.abs(numeric) >= 10 ? numeric.toFixed(0) : numeric.toFixed(2);
  return `${numeric > 0 ? "+" : ""}${rounded}`;
};

// A genuine 20-ATR move is possible but "+20.61σ" reads as broken data.
// Clamp the chip and keep the exact value in the tooltip.
const formatAtr = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  if (Math.abs(numeric) > 9.9) return `${numeric > 0 ? ">+" : "<-"}9.9σ`;
  return `${formatSigned(numeric)}σ`;
};

const TimeframeStrip = ({
  timeframes = [],
  priorDay = {},
  symbol,
  available = true,
  loading = false,
  activeTimeframe,
  selectableTimeframes = [],
  onSelectTimeframe,
}) => {
  if (loading && !timeframes.length) {
    return (
      <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-[10px] text-white/35">
        Loading multi-timeframe context…
      </div>
    );
  }

  if (!available || !timeframes.length) {
    return null;
  }

  const priorLevels = [
    priorDay?.prior_day_high != null ? `PDH ${priorDay.prior_day_high}` : null,
    priorDay?.prior_day_low != null ? `PDL ${priorDay.prior_day_low}` : null,
    priorDay?.prior_day_close != null ? `PDC ${priorDay.prior_day_close}` : null,
  ].filter(Boolean);

  return (
    <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2">
      {/* Chips scroll horizontally rather than wrapping. On a phone the six
          timeframes wrapped into ragged rows and the trailing chip was cut
          off; a single scrollable row keeps the ladder readable top-down. */}
      <div className="-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-white/60">
          {symbol || "Context"}
        </span>

        {timeframes.map((entry) => {
          const trend = TREND_STYLES[entry.trend] || TREND_STYLES.flat;
          const isActive = activeTimeframe === entry.tf;
          const detail = [
            `${entry.tf} · ${entry.trend || "flat"}`,
            entry.n != null ? `${entry.n} bars` : null,
            entry.chg != null ? `change ${formatSigned(entry.chg)}` : null,
            entry.chg_atr != null ? `${formatSigned(entry.chg_atr)} ATR` : null,
            entry.pos != null ? `position in range ${(entry.pos * 100).toFixed(0)}%` : null,
            entry.hi != null && entry.lo != null ? `range ${entry.lo} – ${entry.hi}` : null,
          ]
            .filter(Boolean)
            .join("\n");

          return (
            <span
              key={entry.tf}
              title={detail}
              aria-label={detail}
              className={`flex shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium ${trend} ${
                isActive ? "ring-1 ring-white/40" : ""
              }`}
            >
              <span className="font-semibold">{entry.tf}</span>
              <span>{TREND_GLYPH[entry.trend] || TREND_GLYPH.flat}</span>
              {entry.chg_atr != null ? (
                <span className="opacity-70">{formatAtr(entry.chg_atr)}</span>
              ) : null}
            </span>
          );
        })}

      </div>

      {priorLevels.length ? (
        <div className="truncate text-[10px] text-white/40" title="Prior day levels">
          {priorLevels.join(" · ")}
        </div>
      ) : null}
    </div>
  );
};

export default TimeframeStrip;
